import { Link } from "react-router-dom";
import { BrandLockup } from "@/components/BrandLogo";
import { FileText, Info } from "lucide-react";

// Feb-21: Terms of Service — public, non-editable webpage. Referenced from the
// Play Store listing and the app footer.
export default function Terms() {
  const updated = "September 17, 2026";
  return (
    <div className="min-h-screen bg-[#050914] text-slate-200">
      <header className="max-w-4xl mx-auto px-6 py-8 flex items-center justify-between">
        <Link to="/"><BrandLockup size="sm"/></Link>
        <Link to="/" data-testid="terms-back-home" className="text-xs text-nb-cyan hover:text-white">← Back to Home</Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-20">
        <div className="text-xs uppercase tracking-widest text-nb-cyan mb-2">Legal</div>
        <h1 className="font-heading font-black text-4xl md:text-5xl text-white tracking-tight">Terms of Service</h1>
        <div className="mt-3 text-sm text-slate-400">Last updated: {updated}</div>

        <div className="mt-6 nb-card p-5 border-[#EAB308]/30 flex items-start gap-3">
          <Info size={22} className="text-[#EAB308] shrink-0 mt-0.5"/>
          <div className="text-sm">
            <div className="text-white font-semibold">Please read carefully</div>
            <p className="mt-1 text-slate-300 leading-relaxed">By creating an account or using the Next Billion app, you agree to these Terms. If you do not agree, please do not use the service.</p>
          </div>
        </div>

        <Section title="1. Eligibility">
          <p>You must be at least 18 years old and legally allowed to use crypto financial services in your jurisdiction. You are responsible for confirming that use of Next Billion complies with your local laws.</p>
        </Section>

        <Section title="2. Account & Security">
          <ul className="list-disc list-inside space-y-1">
            <li>You must provide accurate, current information during registration.</li>
            <li>You are responsible for maintaining the confidentiality of your password and any two-factor codes.</li>
            <li>You must notify us immediately if you suspect any unauthorised use of your account.</li>
            <li>Only ONE account per person is permitted. Duplicate accounts may be suspended.</li>
          </ul>
        </Section>

        <Section title="3. Deposits, bots & withdrawals">
          <ul className="list-disc list-inside space-y-1">
            <li>Deposits are on-chain USDT (BEP20 or TRC20) transfers to addresses displayed inside the app. Confirmations follow blockchain rules.</li>
            <li>Once a bot is activated, funds are locked for the bot cycle (typically 7 days) and returned to your Main Wallet automatically at cycle end.</li>
            <li>Daily profit percentages are set by the platform within published ranges and are NOT guaranteed. Past performance does not guarantee future results.</li>
            <li>Withdrawals are subject to the current cooldown (default: one request per 24 hours) and are processed within 24 hours of admin approval.</li>
            <li>Any withdrawal to an incorrect or non-BEP20 address is your sole responsibility. The company cannot recover such transfers.</li>
          </ul>
        </Section>

        <Section title="4. Referral & rank rewards">
          <p>Referral bonuses, level income, rank bonuses, tour rewards, and leadership bonuses are calculated per the rules published in the in-app Plan section. Rules may be updated with 14-day advance notice inside the app. Historical earnings are not affected by rule changes.</p>
        </Section>

        <Section title="5. Company Credit Line">
          <p>The Company Credit Line is an optional, one-time-per-user facility with its own separate ledger. It is <b>never withdrawable</b>. Full terms (10× business target, 50/50 self+direct/team split, 60-day validity) are shown on the Credit page and in the Plan PDF. By accepting a credit line, you accept those rules.</p>
        </Section>

        <Section title="6. Prohibited use">
          <ul className="list-disc list-inside space-y-1">
            <li>Using bots, scripts or automation to abuse referral or bonus systems.</li>
            <li>Creating fake accounts or manipulating team volume.</li>
            <li>Depositing funds obtained illegally, laundering money, or terrorism financing.</li>
            <li>Attempting to access other users&apos; accounts or the admin panel without authorisation.</li>
            <li>Reverse engineering, decompiling, or attacking our infrastructure.</li>
          </ul>
          <p className="mt-3">Any violation results in immediate account suspension and forfeiture of any pending bonuses.</p>
        </Section>

        <Section title="7. Risk disclosure">
          <p>Crypto assets are volatile. Bot profits depend on live market conditions. You may lose part or all of your invested capital. Only invest what you can afford to lose. Nothing in the app constitutes financial or investment advice — you should consult a licensed advisor if unsure.</p>
        </Section>

        <Section title="8. Limitation of liability">
          <p>To the maximum extent permitted by law, Next Billion is not liable for indirect, incidental, or consequential damages, or for losses arising from network delays, wallet errors, or forces outside our control. Our total liability for any claim is limited to the amount you deposited in the 90 days before the claim.</p>
        </Section>

        <Section title="9. Termination">
          <p>You may close your account at any time from the app. We may suspend or terminate accounts that violate these Terms. On termination, any accrued balances that are not the result of prohibited activity will be paid out per normal withdrawal rules.</p>
        </Section>

        <Section title="10. Governing law & contact">
          <p>These Terms are governed by the laws applicable to the Next Billion operating entity. Disputes should first be raised via <a className="text-nb-cyan" href="mailto:support@nextbillion.world">support@nextbillion.world</a> so we can resolve them amicably.</p>
        </Section>

        <div className="mt-10 border-t border-white/10 pt-6 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-3">
          <div>© 2026 Next Billion. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/" className="hover:text-white">Home</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={18} className="text-nb-cyan"/>
        <h2 className="font-heading font-bold text-xl text-white">{title}</h2>
      </div>
      <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
    </section>
  );
}
